import React, { useState, useRef, useMemo } from "react";
import ChatListItem from "./ChatListItem";
import FilterDropdown from "./FilterDropdown";
import { useSelector } from "react-redux";

const Sidebar = ({
  onSelectUser,
  conversationsList,
  onFilterChange,
  setIsChecked,
  isChecked,
  activeTab,
  setActiveTab,
}) => {
  const { messengerCheckInClientsList, unreadCheckInCount, isLoading } =
    useSelector((state) => state.messenger);

  // const [activeTab, setActiveTab] = useState("messages");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const sidebarRef = useRef(null);

  // Process conversations data
  const filteredConversations = useMemo(() => {
    return (
      conversationsList?.map((chat) => ({
        id: chat.clientId,
        name: chat.clientName,
        conversationId: chat.conversationId,
        participantId: chat.clientParticipantId,
        friendlyName: chat.friendlyName,
        clientMessageStatus: chat.lastMessageTime,
        unreadMessageCount: chat.unreadCount,
        dateCreated: chat.dateCreated,
      })) || []
    );
  }, [conversationsList]);

  // Process check-ins data
  const filteredCheckIns = useMemo(() => {
    return (
      messengerCheckInClientsList?.map((checkIn) => ({
        id: checkIn.clientId,
        name: checkIn.clientName,
        conversationId: checkIn.conversationId,
        participantId: checkIn.clientParticipantId,
        friendlyName: checkIn.friendlyName,
        clientMessageStatus: checkIn.lastMessageTime,
        unreadMessageCount: checkIn.unreadCount,
        dateCreated: checkIn.dateCreated,
      })) || []
    );
  }, [messengerCheckInClientsList]);

  // Filter conversations based on search text (only if 3+ chars)
  const searchedConversations = useMemo(() => {
    if (!searchText || searchText.length < 3) return filteredConversations;
    return filteredConversations.filter((chat) =>
      chat.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [filteredConversations, searchText]);

  // Filter check-ins based on search text (only if 3+ chars)
  const searchedCheckIns = useMemo(() => {
    if (!searchText || searchText.length < 3) return filteredCheckIns;
    return filteredCheckIns.filter((checkIn) =>
      checkIn.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [filteredCheckIns, searchText]);

  const handleFilterClick = (filterOption) => {
    onFilterChange(filterOption);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Reset search when changing tabs
    setSearchText("");
  };

  const handleFilterClickUnseen = (e) => {
    setIsChecked(e.target.checked);
    if (e.target.checked) {
      onFilterChange("onlyunseen");
    } else {
      onFilterChange("");
    }
  };

  return (
    <div className="w-25 bg-white shadow-md flex flex-col">
      {/* Tabs Section */}
      <div className="flex justify-between items-center px-4 p-4 border-b relative">
        {/* Tabs */}
        <div className="flex">
          <span
            className={`font-semibold cursor-pointer px-2 ${
              activeTab === "messages"
                ? "border-b-2 border-black"
                : "text-gray-500"
            }`}
            onClick={() => handleTabChange("messages")}
          >
            Messages
          </span>
          <span
            className={`font-semibold cursor-pointer px-2 relative ${
              activeTab === "checkins"
                ? "border-b-2 border-black"
                : "text-gray-500"
            }`}
            onClick={() => handleTabChange("checkins")}
          >
            Check-ins
            {unreadCheckInCount > 0 && (
              <span className="ml-2 bg-red-500 text-white font-normal rounded-full px-1 py-0.5">
                {unreadCheckInCount}
              </span>
            )}
          </span>
        </div>

        {/* Filter Icon */}
        {activeTab === "messages" && (
          <div className="relative">
            <button onClick={() => setShowFilters(!showFilters)}>⚙️</button>
            {showFilters && (
              <FilterDropdown
                onFilterClick={handleFilterClick}
                setShowFilters={setShowFilters}
                showFilters={showFilters}
                selectedFilter={selectedFilter}
                setSelectedFilter={setSelectedFilter}
              />
            )}
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="p-2 border-b bg-gray-100">
        <input
          type="text"
          className="w-full p-2 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder={`Search ${
            activeTab === "messages" ? "clients" : "check-ins"
          }`}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      {/* Loading & Error State */}
      {/* {!isLoading && error && activeTab !== "messages" && (
        <p className="text-center p-4 text-red-500">{error}</p>
      )} */}

      {isLoading && <p className="text-center p-4">Loading...</p>}

      {/* Messages List */}
      <div ref={sidebarRef} className="overflow-y-auto flex-1 custom-scrollbar">
        {activeTab === "messages" ? (
          <>
            <div className="p-3">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={isChecked}
                  className="w-5 h-5"
                  onChange={handleFilterClickUnseen}
                />
                <span>Only unseen</span>
              </label>
            </div>

            {searchedConversations.length > 0 ? (
              searchedConversations.map((chat) => (
                <ChatListItem
                  key={`${chat.id + chat.conversationId}`}
                  name={chat.name}
                  message="Tap to chat"
                  // lastMessageTime={chat.clientMessageStatus}
                  lastMessageTime={
                    selectedFilter === "startDate"
                      ? chat.dateCreated
                      : chat.clientMessageStatus
                  }
                  unreadMessageCount={chat.unreadMessageCount}
                  onClick={() => onSelectUser(chat)}
                />
              ))
            ) : (
              <p className="text-center p-4 text-gray-500">
                {searchText
                  ? "No matching clients found"
                  : "No clients available"}
              </p>
            )}
          </>
        ) : (
          <>
            {searchedCheckIns.length > 0 ? (
              searchedCheckIns.map((checkIn) => (
                <ChatListItem
                  key={`${checkIn.id + checkIn.conversationId}`}
                  name={checkIn.name}
                  message={checkIn.message}
                  lastMessageTime={checkIn.clientMessageStatus}
                  unreadMessageCount={checkIn.unreadMessageCount}
                  tag={checkIn.tag}
                  onClick={() => onSelectUser(checkIn)}
                />
              ))
            ) : (
              <p className="text-center p-4 text-gray-500">
                {searchText
                  ? "No matching check-ins found"
                  : "No check-ins available"}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
